import firebase from 'firebase/app';
import 'firebase/firestore';
import React, { useCallback, useState, useEffect } from 'react';
import styled from 'styled-components';
import { range } from '../utils/range';
import { memoNamed } from '../utils/react/memo-named';
import { NamesUser } from './NamesUser';
import { NamesEdit } from './NamesEdit';
import { userWithDefault, User, hasPrivilege } from '../types/user';
import { StringInput } from './common/input';
import { NamesPrivilege } from './NamesPrivilege';
import { Root } from './common/styled';

const currentYear = new Date().getFullYear() + 1;
const options = range(1971, currentYear).map((y) => (
  <option key={y} value={y.toString()}>
    {`${y}年度入会`}
  </option>
));

const Message = styled.div`
  color: red;
`;

interface Props {
  isAdmin: boolean;
}

export const Names = memoNamed<Props>('Names', (props) => {
  const [nameList, setNameList] = useState<string[]>([]);
  const [userList, setUserList] = useState<object>({});
  const [message, setMessage] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [enterYear, setEnterYear] = useState<string | 'none'>('none');
  const [showAdditionalInfo, setShowAdditionalInfo] = useState<string>('');
  const [seiKana, setSeiKana] = useState<string>('');
  const [editable, setEditable] = useState<string>('');
  const [showPayInfo, setShowPayInfo] = useState<string>('');
  const [privilege, setPrivilege] = useState<boolean>(false);

  useEffect(() => {
    const user = firebase.auth().currentUser;
    const db = firebase.firestore();
    db.collection('Users')
      .where('userID', '==', user?.uid)
      .get()
      .then((snapshot) => {
        if (snapshot.size === 1) {
          setPrivilege(hasPrivilege(snapshot.docs[0].data().privilegePeriod));
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const searchNameByEnterYear = useCallback(
    (event?: React.ChangeEvent<HTMLSelectElement>) => {
      const year = event?.target.value ?? enterYear;
      setEnterYear(year);
      setSeiKana('');
      setEditable('');
      setShowPayInfo('');
      setNameList([]);
      const db = firebase.firestore();
      setMessage('');
      setIsUpdating(true);
      db.collection('Users')
        .where('enterYear', '==', year)
        .orderBy('seiKana')
        .get()
        .then((snapshot) => {
          setMessage('');
          if (snapshot.empty) {
            setMessage('データが存在しません。');
          }
          const _nameList: string[] = range(0, snapshot.size).map(String);
          const _userList = {};
          snapshot.docs.forEach((doc, i) => {
            const data: User = userWithDefault(doc.data());
            _nameList[i] = doc.id;
            _userList[doc.id] = data;
          });
          setUserList(_userList);
          setNameList(_nameList);
          setIsUpdating(false);
        })
        .catch((error) => {
          console.error(error);
          setMessage('データの取得に失敗しました。');
          setIsUpdating(false);
        });
    },
    [enterYear]
  );

  const searchNameBySeiKana = useCallback(
    (event?: React.FocusEvent<HTMLInputElement>) => {
      const seiKanaValue: string = event?.target.value ?? seiKana;
      setNameList([]);
      setMessage('');
      setEnterYear('none');
      setEditable('');
      setShowPayInfo('');
      setIsUpdating(true);
      const db = firebase.firestore();
      db.collection('Users')
        .where('seiKana', '==', seiKanaValue)
        .get()
        .then((snapshot) => {
          setMessage('');
          if (snapshot.empty) {
            setMessage('データが存在しません。');
          }
          const _nameList: string[] = range(0, snapshot.size).map(String);
          const _userList = {};
          snapshot.docs.forEach((doc, i) => {
            const data: User = userWithDefault(doc.data());
            _nameList[i] = doc.id;
            _userList[doc.id] = data;
          });
          setUserList(_userList);
          setNameList(_nameList);
          setIsUpdating(false);
        })
        .catch(() => {
          setMessage('データの取得に失敗しました。');
          setIsUpdating(false);
        });
    },
    [seiKana]
  );

  const updateUserList = useCallback(() => {
    setEditable('');
    setShowPayInfo('');
    setShowAdditionalInfo('');
    //setUserList([]);
    if (enterYear === 'none' && seiKana !== '') {
      searchNameBySeiKana();
    } else if (enterYear !== 'none' && seiKana === '') {
      searchNameByEnterYear();
    }
  }, [enterYear, seiKana, searchNameBySeiKana, searchNameByEnterYear]);

  return (
    <Root>
      <h3>検索</h3>
      入会年度で探す{' '}
      <select value={enterYear} onChange={searchNameByEnterYear}>
        <option key={0} value='none'></option>
        {options}
      </select>
      <br />
      苗字(ひらがな)で探す{' '}
      <StringInput
        type='text'
        value={seiKana}
        onValueChange={setSeiKana}
        onBlur={searchNameBySeiKana}
      />
      {isUpdating ? ' 通信中...' : ''}
      <hr />
      {showPayInfo !== '' ? (
        //支払情報編集画面
        <NamesPrivilege
          docID={showPayInfo}
          user={userList[showPayInfo]}
          updateUserList={updateUserList}
        />
      ) : undefined}
      {editable !== '' ? (
        //名簿情報 編集画面
        <NamesEdit
          docID={editable}
          user={userList[editable]}
          updateUserList={updateUserList}
        />
      ) : undefined}
      {showPayInfo === '' && editable === '' ? (
        <div>
          <h3>検索結果</h3>
          <Message>{message}</Message>
          {nameList.map((n, i) => (
            <div key={i}>
              <NamesUser
                docID={nameList[i]}
                user={userList[n]}
                showAdditionalInfo={showAdditionalInfo}
                setShowAdditionalInfo={setShowAdditionalInfo}
                editable={editable}
                setEditable={setEditable}
                isAdmin={props.isAdmin}
                setShowPayInfo={setShowPayInfo}
                hasPrivilege={privilege}
              />
              <br />
            </div>
          ))}
        </div>
      ) : undefined}
    </Root>
  );
});
